import { useState, FormEvent, Fragment } from 'react';
import {
  FileText,
  Download,
  Sparkles,
  Paperclip,
  ArrowUp,
  EllipsisVertical,
  List,
  ChevronDown,
  Maximize2,
  Loader,
  CircleCheck,
  Circle,
} from 'lucide-react';
import { usePrototype3 } from '../Prototype3Context';
import { SECTIONS, SectionStatus, SectionMeta } from '../prototype3Data';
import DraftBody from '../../_shared/DraftBody';
import useUserDetails from '../../../hooks/useUserDetails';

interface SectionStatusIconProps {
  status: SectionStatus;
}

const SectionStatusIcon = ({ status }: SectionStatusIconProps) => {
  if (status === 'complete') return <CircleCheck className="w-3 h-3 text-[#16a34a] shrink-0" />;
  if (status === 'in-progress') return <Loader className="w-3 h-3 text-[#3b82f6] shrink-0 animate-spin" />;
  return <Circle className="w-3 h-3 text-[#737373] shrink-0" />;
};

interface SectionStatusLabelProps {
  status: SectionStatus;
}

const SectionStatusLabel = ({ status }: SectionStatusLabelProps) => {
  if (status === 'complete') return <span className="text-[11px] font-medium text-[#16a34a]">Complete</span>;
  if (status === 'in-progress') return <span className="text-[11px] font-medium text-[#3b82f6]">In Progress</span>;
  return <span className="text-[11px] text-[#737373]">Not Started</span>;
};

const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

interface ChatMessageRowProps {
  role: 'user' | 'assistant';
  content: string;
  senderLabel: string;
  time: string;
  isTyping?: boolean;
}

const ChatMessageRow = ({ role, content, senderLabel, time, isTyping }: ChatMessageRowProps) => {
  const isAssistant = role === 'assistant';
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center ${
            isAssistant ? 'bg-[#171717]' : 'bg-[#6366f1]'
          }`}
        >
          {isAssistant && <Sparkles className="w-3 h-3 text-white" />}
        </div>
        <span className="text-[13px] font-semibold text-[#0a0a0a]">{senderLabel}</span>
        <span className="text-[12px] text-[#737373]">{time}</span>
      </div>
      <div className={`rounded-lg px-4 py-3 ${isAssistant ? 'bg-[#f5f5f5]' : 'bg-[#6366f1]/10'}`}>
        {isAssistant && !isTyping ? (
          <DraftBody text={content} />
        ) : (
          <p className="text-[13px] text-[#0a0a0a] leading-[1.5] whitespace-pre-wrap">{content}</p>
        )}
        {isTyping && (
          <div className="flex items-center gap-1.5 mt-2">
            <Loader className="w-3 h-3 text-[#3b82f6] animate-spin" />
            <span className="text-[11px] text-[#3b82f6] font-medium">Updating report...</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface ReportPaneProps {
  section: SectionMeta;
  status: SectionStatus;
  draft: string;
  isPending: boolean;
}

const ReportPane = ({ section, status, draft, isPending }: ReportPaneProps) => {
  const showDraftingBadge = isPending && status !== 'complete';
  return (
    <div className="flex flex-col gap-6 p-8 overflow-y-auto flex-1">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[20px] font-bold text-[#0a0a0a]">{section.title}</h2>
          {showDraftingBadge && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#3b82f6]/10">
              <Loader className="w-3.5 h-3.5 text-[#3b82f6] animate-spin" />
              <span className="text-[11px] font-semibold text-[#3b82f6]">Drafting...</span>
            </div>
          )}
        </div>
        <p className="text-[13px] text-[#737373] leading-[1.6]">{section.description}</p>
      </div>

      <hr className="border-[#e5e5e5]" />

      <DraftBody text={draft} />

      {showDraftingBadge && (
        <div className="flex flex-col gap-1.5">
          <div className="h-3 rounded bg-[#f5f5f5] w-full" />
          <div className="h-3 rounded bg-[#f5f5f5] w-[280px]" />
        </div>
      )}
    </div>
  );
};

const Screen02DraftBuilder = () => {
  const {
    chatHistory,
    isChatPending,
    sendUserMessage,
    sendChat,
    drafts,
    sectionStatuses,
    currentSectionId,
    setCurrentSectionId,
    sectionDropdownOpen,
    toggleSectionDropdown,
    setSectionDropdownOpen,
  } = usePrototype3();
  const [input, setInput] = useState('');
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const userName = userDetails?.name?.trim() || 'You';

  const section = SECTIONS.find((s) => s.id === currentSectionId) ?? SECTIONS[1];
  const status = sectionStatuses[section.id] ?? section.status;
  const draft = drafts[section.id] ?? '';
  const completeCount = SECTIONS.filter((s) => (sectionStatuses[s.id] ?? s.status) === 'complete').length;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    await sendUserMessage(message);
  };

  const handlePickSection = async (id: string) => {
    setSectionDropdownOpen(false);
    if (id === currentSectionId) return;
    setCurrentSectionId(id);
    const targetSection = SECTIONS.find((s) => s.id === id);
    if (!targetSection) return;
    const targetDraft = drafts[id] ?? '';
    if (targetDraft.trim().length === 0 && (sectionStatuses[id] ?? targetSection.status) !== 'complete') {
      await sendChat(`Switching to "${targetSection.title}". Please draft this section.`, {
        intent: 'draft',
        active_section_id: id,
        current_draft: '',
        ui_state: { panel: 'chat-open', last_user_action: 'switch-and-draft' },
        response_hint: { shape: 'draft-paragraph' },
      });
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-[#fafafa] font-['Inter']">
      <header className="flex items-center gap-3 px-6 h-14 border-b border-[#e5e5e5] bg-[#fafafa] shrink-0">
        <FileText className="w-5 h-5 text-[#0a0a0a]" />
        <span className="text-[15px] font-semibold text-[#0a0a0a]">EOAA Report Builder</span>
        <span className="text-sm text-[#737373]">|</span>
        <span className="text-sm text-[#737373]">Issaquah School District - 2023-24</span>
        <div className="flex-1" />
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 text-[#0a0a0a] transition-colors"
        >
          <Download className="w-4 h-4" />
          Export Draft
        </button>
      </header>

      <div className="flex flex-1 min-h-0">
        <div className="flex flex-col w-[480px] shrink-0 border-r border-[#e5e5e5] bg-[#fafafa]">
          <div className="flex items-center gap-2.5 px-5 h-[52px] border-b border-[#e5e5e5] shrink-0">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-[#f5f5f5] text-[#737373] border border-[#e5e5e5]">
              {isChatPending ? 'Thinking…' : 'Active'}
            </span>
            <div className="flex-1" />
            <EllipsisVertical className="w-4 h-4 text-[#737373]" />
          </div>

          <div className="flex flex-col gap-4 flex-1 overflow-y-auto px-5 py-4">
            <ChatMessageRow
              role="assistant"
              senderLabel="AI Assistant"
              time="Just now"
              content="I can help you draft, review, and refine each section of the Educational Outcomes Audit & Analysis for Issaquah School District. Tell me where you'd like to start — or I can walk you through it section by section."
            />
            {chatHistory.map((m, i) => (
              <ChatMessageRow
                key={i}
                role={m.role}
                senderLabel={m.role === 'assistant' ? 'AI Assistant' : userName}
                time={formatTime(new Date())}
                content={m.content}
              />
            ))}
            {isChatPending && (
              <ChatMessageRow
                role="assistant"
                senderLabel="AI Assistant"
                time="Just now"
                content="Drafting…"
                isTyping
              />
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2.5 px-4 h-16 border-t border-[#e5e5e5] shrink-0"
          >
            <button type="button" className="shrink-0 hover:opacity-70 transition-opacity">
              <Paperclip className="w-[18px] h-[18px] text-[#737373]" />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about the report or give directions..."
              disabled={isChatPending}
              className="flex-1 h-10 px-3 text-[13px] text-[#0a0a0a] placeholder:text-[#737373] rounded-md bg-[#fafafa] border border-[#e5e5e5] outline-none focus:border-[#a3a3a3] transition-colors"
            />
            <button
              type="submit"
              disabled={!input.trim() || isChatPending}
              className="flex items-center justify-center w-9 h-9 rounded-md bg-[#171717] shrink-0 hover:bg-[#2a2a2a] disabled:opacity-40 transition-colors"
            >
              <ArrowUp className="w-[18px] h-[18px] text-white" />
            </button>
          </form>
        </div>

        <div className="flex flex-col flex-1 min-w-0 bg-[#fafafa] relative">
          <div className="flex items-center gap-3 px-6 h-[52px] border-b border-[#e5e5e5] shrink-0">
            <FileText className="w-[18px] h-[18px] text-[#0a0a0a]" />
            <span className="text-sm font-semibold text-[#0a0a0a]">Live Report Draft</span>
            <button
              type="button"
              onClick={toggleSectionDropdown}
              className={`flex items-center gap-2 px-3 py-[5px] rounded-md border transition-colors ${
                sectionDropdownOpen ? 'bg-[#f5f5f5] border-[#e5e5e5]' : 'bg-[#fafafa] border-[#e5e5e5] hover:bg-gray-50'
              }`}
            >
              <List className="w-3.5 h-3.5 text-[#0a0a0a]" />
              <span className="text-[13px] font-medium text-[#0a0a0a]">
                {section.index} of {SECTIONS.length} sections
              </span>
              <ChevronDown className={`w-3.5 h-3.5 ${sectionDropdownOpen ? 'text-[#0a0a0a]' : 'text-[#737373]'}`} />
            </button>
            <span className="text-[12px] text-[#737373]">
              {completeCount}/{SECTIONS.length} complete
            </span>
            <div className="flex-1" />
            <button type="button" className="hover:opacity-70 transition-opacity" aria-label="Maximize">
              <Maximize2 className="w-4 h-4 text-[#737373]" />
            </button>
          </div>

          {sectionDropdownOpen && (
            <div className="absolute top-[52px] left-[185px] z-10 w-[364px] rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_4px_8px_-2px_rgba(0,0,0,0.09),0_8px_12px_-2px_rgba(0,0,0,0.14)] p-1.5">
              {SECTIONS.map((s, i) => {
                const sStatus = sectionStatuses[s.id] ?? s.status;
                const isCurrent = s.id === currentSectionId;
                return (
                  <Fragment key={s.id}>
                    {i === 1 && (
                      <div className="py-1">
                        <hr className="border-[#e5e5e5]" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handlePickSection(s.id)}
                      className={`flex w-full items-center gap-2 px-2.5 py-[7px] rounded text-left ${
                        isCurrent ? 'bg-[#f5f5f5]' : 'hover:bg-[#f5f5f5]'
                      }`}
                    >
                      <SectionStatusIcon status={sStatus} />
                      <span className={`text-[13px] text-[#0a0a0a] ${isCurrent ? 'font-semibold' : ''}`}>
                        {s.title}
                      </span>
                      <div className="flex-1" />
                      <SectionStatusLabel status={sStatus} />
                    </button>
                  </Fragment>
                );
              })}
            </div>
          )}

          <ReportPane section={section} status={status} draft={draft} isPending={isChatPending} />
        </div>
      </div>
    </div>
  );
};

export default Screen02DraftBuilder;
